import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../App";
import { colorForEvent, assigneesForEvent, ALL_STAFF_COLOR, colorForStaff } from "../../lib/staffColors";

// ── time helpers ──
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const period = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m || 0).padStart(2, "0")} ${period}`;
};
const eventTimeLabel = (event) => {
  const start = fmtTime(event.start_time);
  const end = fmtTime(event.end_time);
  if (start && end) return `${start} – ${end}`;
  return start || end || "";
};

const EVENT_CATEGORIES = [
  { type: "holiday", label: "Holiday/Break" },
  { type: "snow_day", label: "Snow Day" },
  { type: "meeting", label: "Meeting" },
  { type: "event", label: "Event" },
  { type: "themed_week", label: "Themed Week" },
  { type: "tour", label: "Tour" },
  { type: "staff_pto", label: "Staff PTO" },
  { type: "other", label: "Other" },
  { type: "birthday", label: "Birthday" },
];

const CALENDAR_VIEWS = [
  { v: "month", label: "Month" },
  { v: "agenda", label: "Agenda" },
  { v: "3day", label: "3 Day" },
  { v: "week", label: "Week" },
];

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); d.setHours(0, 0, 0, 0); return d; };
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const withAlpha = (hex, alpha) => hex + Math.round(alpha * 255).toString(16).padStart(2, "0");
const firstName = (name) => (name || "").trim().split(/\s+/)[0] || name;

// Monday of the work-week containing `date` (weekends roll to the coming Monday).
const workMonday = (date) => {
  const x = new Date(date); x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  return addDays(x, day === 0 ? 1 : day === 6 ? 2 : 1 - day);
};

const EditDeleteButtons = ({ event, isAdmin, onEditEvent, handleDeleteEvent, onDone, testid }) => {
  if (!isAdmin() || event.is_birthday || event.read_only) return null;
  return (
    <div className="flex items-center gap-1 shrink-0">
      <Button variant="ghost" size="sm" onClick={() => { onEditEvent(event); onDone && onDone(); }} data-testid={`${testid}-${event.id}`} className="h-8 w-8 p-0 text-[#00305E] hover:bg-blue-100">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>
      </Button>
      <Button variant="ghost" size="sm" onClick={() => { handleDeleteEvent(event.id); onDone && onDone(); }} className="h-8 w-8 p-0 text-red-600 hover:bg-red-100">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
      </Button>
    </div>
  );
};

export function SchoolCalendarTab({
  selectedDate, setSelectedDate, viewMode, setViewMode,
  getEventsForDate,
  getEventTypeColor, handleDeleteEvent, onEditEvent, onAddEventForDate, isAdmin,
  staff = [], staffById = {}, activeStaffIds = [], staffFilter = "all", setStaffFilter, currentUserId,
  searchEvents,
}) {
  const { user } = useAuth();
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dayDialogDate, setDayDialogDate] = useState(null);
  const [calendarView, setCalendarView] = useState("month");
  // ── event search ──
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = idle, [] = searched/empty
  const [searching, setSearching] = useState(false);
  const searchBoxRef = useRef(null);
  const storageKey = `mev_hidden_cats_${user?.id || "anon"}`;
  const [hiddenCategories, setHiddenCategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });

  const toggleCategory = (type) => {
    setHiddenCategories((prev) => {
      const next = prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type];
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // ── filtering ──
  const matchesStaffFilter = (e) => {
    if (staffFilter !== "mine" || !currentUserId) return true;
    return e.created_by === currentUserId || (e.notify_staff || []).includes(currentUserId);
  };
  const isVisible = (e) => !hiddenCategories.includes(e.type) && matchesStaffFilter(e);
  const eventsForDate = (date) => (getEventsForDate(date) || []).filter(isVisible)
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const eventColor = (e) => colorForEvent(e, staffById, activeStaffIds);

  const openDay = (date) => { setDayDialogDate(date); setDayDialogOpen(true); };

  // Debounced event search across all dates.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q || !searchEvents) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchEvents(q);
        setSearchResults((res || []).filter(isVisible));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Close results when clicking outside the search box.
  useEffect(() => {
    const onDoc = (e) => { if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setSearchResults(null); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const jumpToEvent = (event) => {
    const d = new Date(`${event.date}T00:00:00`);
    if (isNaN(d.getTime())) return;
    setSelectedDate(d);
    setSearchResults(null);
    setSearchQuery("");
    openDay(d);
  };

  // ── period navigation ──
  const shiftPeriod = (dir) => {
    if (calendarView === "month" || calendarView === "agenda") {
      const d = new Date(selectedDate); d.setMonth(d.getMonth() + dir); setSelectedDate(d);
    } else if (calendarView === "3day") {
      setSelectedDate(addDays(selectedDate, dir * 3));
    } else {
      setSelectedDate(addDays(selectedDate, dir * 7));
    }
  };

  const periodLabel = () => {
    if (calendarView === "month" || calendarView === "agenda")
      return selectedDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    if (calendarView === "3day") {
      const end = addDays(selectedDate, 2);
      return `${selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    }
    const mon = workMonday(selectedDate); const fri = addDays(mon, 4);
    return `${mon.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${fri.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  // ── render helpers (plain functions to avoid remounting) ──
  const renderEventChip = (event) => {
    const c = eventColor(event);
    return (
      <span key={event.id} role="button" data-testid={`cal-chip-${event.id}`}
        className="w-full flex items-center gap-1 px-1.5 py-0.5 rounded text-left truncate"
        style={{ backgroundColor: withAlpha(c, 0.16) }} title={event.title}>
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: c }} />
        {fmtTime(event.start_time) && <span className="text-[10px] font-semibold shrink-0" style={{ color: c }}>{fmtTime(event.start_time)}</span>}
        <span className="text-[11px] text-slate-800 truncate">{event.title}</span>
      </span>
    );
  };

  const renderAssigneeDots = (event) => {
    const assignees = event.notify_staff || [];
    const isAll = activeStaffIds.length > 0 && assignees.length > 0 && activeStaffIds.every((id) => assignees.includes(id));
    if (isAll) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-slate-600">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ALL_STAFF_COLOR }} /> All
        </span>
      );
    }
    const people = assigneesForEvent(event, staffById);
    if (people.length === 0) return null;
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {people.map((s) => (
          <span key={s.id} className="inline-flex items-center gap-1 text-xs text-slate-600">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colorForStaff(s) }} /> {firstName(s.name)}
          </span>
        ))}
      </span>
    );
  };

  const monthMatrix = () => {
    const first = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const gridStart = addDays(first, -first.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  };

  const renderMonthView = () => (
    <Card data-testid="calendar-month-view">
      <CardContent className="p-2 sm:p-3">
        <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
          {DOW.map((d) => <div key={d} className="bg-slate-50 py-2 text-center text-xs font-semibold text-slate-500">{d}</div>)}
          {monthMatrix().map((date) => {
            const inMonth = date.getMonth() === selectedDate.getMonth();
            const evts = eventsForDate(date);
            const isToday = sameDay(date, today);
            return (
              <div key={toISO(date)} role="button" tabIndex={0} onClick={() => openDay(date)}
                onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); openDay(date); } }}
                data-testid={`cal-day-${toISO(date)}`}
                className={`min-h-[92px] sm:min-h-[110px] bg-white p-1 text-left align-top hover:bg-slate-50 transition-colors cursor-pointer ${inMonth ? "" : "opacity-40"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`inline-flex items-center justify-center text-xs font-semibold ${isToday ? "bg-[#00305E] text-white w-6 h-6 rounded-full" : "text-slate-600"}`}>{date.getDate()}</span>
                </div>
                <div className="space-y-0.5">
                  {evts.slice(0, 3).map((e) => renderEventChip(e))}
                  {evts.length > 3 && <span className="text-[10px] text-slate-500 pl-1">+{evts.length - 3} more</span>}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const renderDayColumns = (dates, testid) => (
    <Card data-testid={testid}>
      <CardContent className="p-2 sm:p-3">
        <div className="grid gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200" style={{ gridTemplateColumns: `repeat(${dates.length}, minmax(0, 1fr))` }}>
          {dates.map((date) => (
            <div key={`h-${toISO(date)}`} className="bg-slate-50 py-2 text-center">
              <p className="text-xs font-semibold text-slate-500 uppercase">{date.toLocaleDateString("en-US", { weekday: "short" })}</p>
              <p className={`text-lg font-bold ${sameDay(date, today) ? "text-[#00305E]" : "text-slate-700"}`}>{date.getDate()}</p>
            </div>
          ))}
          {dates.map((date) => {
            const evts = eventsForDate(date);
            return (
              <div key={`c-${toISO(date)}`} className="bg-white p-1.5 min-h-[280px] align-top">
                {evts.length > 0 ? (
                  <div className="space-y-1">
                    {evts.map((e) => {
                      const c = eventColor(e);
                      return (
                        <button key={e.id} type="button" onClick={() => openDay(date)} data-testid={`cal-chip-${e.id}`}
                          className="w-full text-left rounded-md p-1.5 hover:opacity-90 transition-opacity" style={{ backgroundColor: withAlpha(c, 0.14), borderLeft: `3px solid ${c}` }}>
                          {eventTimeLabel(e) && <p className="text-[10px] font-semibold" style={{ color: c }}>{eventTimeLabel(e)}</p>}
                          <p className="text-xs font-medium text-slate-800 leading-tight">{e.title}</p>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    {isAdmin() && onAddEventForDate && (
                      <button type="button" onClick={() => onAddEventForDate(toISO(date))} data-testid={`col-add-${toISO(date)}`} className="text-xs text-slate-400 hover:text-[#00305E]">+ Add</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  const renderAgendaView = () => {
    const first = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
    const rows = [];
    for (let i = 0; i < daysInMonth; i++) {
      const date = addDays(first, i);
      const evts = eventsForDate(date);
      if (evts.length > 0) rows.push({ date, evts });
    }
    return (
      <Card data-testid="calendar-agenda-view">
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">No events this month</p>
          ) : (
            <div className="divide-y">
              {rows.map(({ date, evts }) => (
                <div key={toISO(date)} className="flex items-stretch">
                  <div className={`w-20 shrink-0 flex flex-col items-center justify-center py-4 border-r ${sameDay(date, today) ? "bg-[#00305E]/5" : ""}`}>
                    <span className="text-[11px] font-semibold text-slate-500 uppercase">{date.toLocaleDateString("en-US", { weekday: "short" })}</span>
                    <span className={`text-2xl font-bold ${sameDay(date, today) ? "text-[#00305E]" : "text-slate-700"}`}>{date.getDate()}</span>
                    <span className="text-[10px] text-slate-400 uppercase">{date.toLocaleDateString("en-US", { month: "short" })}</span>
                  </div>
                  <div className="flex-1 divide-y">
                    {evts.map((e) => {
                      const c = eventColor(e);
                      return (
                        <div key={e.id} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`agenda-row-${e.id}`}>
                          <span className="w-24 shrink-0 text-xs font-medium text-slate-500 pt-0.5">{eventTimeLabel(e) || "All Day"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c }} />
                              <p className="font-medium text-slate-800 truncate">{e.title}</p>
                            </div>
                            <div className="mt-1 pl-4">{renderAssigneeDots(e)}</div>
                            {e.notes && <p className="text-xs text-muted-foreground mt-1 pl-4">{e.notes}</p>}
                          </div>
                          <EditDeleteButtons event={e} isAdmin={isAdmin} onEditEvent={onEditEvent} handleDeleteEvent={handleDeleteEvent} testid="edit-event-agenda" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const threeDayDates = [0, 1, 2].map((i) => addDays(selectedDate, i));
  const weekDates = Array.from({ length: 5 }, (_, i) => addDays(workMonday(selectedDate), i));
  const dayDialogEvents = dayDialogDate ? eventsForDate(dayDialogDate) : [];
  const activeStaff = staff.filter((s) => s.status !== "former" && s.status !== "deleted");

  return (
    <div className="space-y-4">
      {/* Event search */}
      <div className="relative" ref={searchBoxRef} data-testid="calendar-search">
        <div className="relative">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          <input
            type="text" value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => { if (searchQuery.trim() && searchResults) setSearchResults(searchResults); }}
            placeholder="Search meetings &amp; events (e.g. KG's IEP meeting)…"
            data-testid="calendar-search-input"
            className="w-full h-10 rounded-lg border border-slate-300 bg-white pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#00305E]/30 focus:border-[#00305E]"
          />
          {searchQuery && (
            <button type="button" onClick={() => { setSearchQuery(""); setSearchResults(null); }} data-testid="calendar-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 h-6 w-6 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
        {searchQuery.trim() && (
          <div data-testid="calendar-search-results" className="absolute z-30 mt-1 w-full max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
            {searching ? (
              <div className="px-4 py-3 text-sm text-muted-foreground">Searching…</div>
            ) : (searchResults && searchResults.length > 0) ? (
              searchResults.map((e) => {
                const c = eventColor(e);
                const d = new Date(`${e.date}T00:00:00`);
                const dateLabel = isNaN(d.getTime()) ? e.date : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
                return (
                  <button key={e.id} type="button" onClick={() => jumpToEvent(e)} data-testid={`calendar-search-result-${e.id}`}
                    className="w-full text-left px-3 py-2.5 border-b last:border-b-0 hover:bg-slate-50 flex items-start gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: c }} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-800 truncate">{e.title}</span>
                      <span className="block text-xs text-slate-500">{dateLabel}{eventTimeLabel(e) ? ` · ${eventTimeLabel(e)}` : ""}</span>
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-3 text-sm text-muted-foreground" data-testid="calendar-search-empty">No meetings or events match “{searchQuery.trim()}”.</div>
            )}
          </div>
        )}
      </div>

      {/* Toolbar row 1: nav + period + view toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shiftPeriod(-1)} data-testid="cal-prev" className="h-9 w-9 p-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())} data-testid="cal-today" className="h-9">Today</Button>
          <Button variant="outline" size="sm" onClick={() => shiftPeriod(1)} data-testid="cal-next" className="h-9 w-9 p-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </Button>
          <span className="text-lg font-bold text-[#00305E] ml-1" data-testid="cal-period-label">{periodLabel()}</span>
        </div>

        <div className="inline-flex rounded-lg border overflow-hidden bg-white" data-testid="calendar-view-switcher">
          {CALENDAR_VIEWS.map(({ v, label }) => (
            <button key={v} type="button" onClick={() => setCalendarView(v)} data-testid={`calendar-view-${v}`}
              className={`px-4 py-2 text-sm font-medium transition-colors border-r last:border-r-0 ${calendarView === v ? "bg-[#00305E] text-white" : "text-[#00305E] hover:bg-slate-50"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar row 2: public/full + staff filter */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} data-testid="calendar-view-select" className="h-9 rounded-md border px-3 text-sm bg-white">
          <option value="public">Public Calendar</option>
          <option value="full">Full Calendar</option>
        </select>

        {setStaffFilter && (
          <div className="inline-flex rounded-lg border overflow-hidden bg-white" data-testid="staff-filter-toggle">
            <button type="button" onClick={() => setStaffFilter("all")} data-testid="staff-filter-all"
              className={`px-4 py-2 text-sm font-medium transition-colors border-r ${staffFilter === "all" ? "bg-[#00305E] text-white" : "text-[#00305E] hover:bg-slate-50"}`}>All events</button>
            <button type="button" onClick={() => setStaffFilter("mine")} data-testid="staff-filter-mine"
              className={`px-4 py-2 text-sm font-medium transition-colors ${staffFilter === "mine" ? "bg-[#00305E] text-white" : "text-[#00305E] hover:bg-slate-50"}`}>My events</button>
          </div>
        )}
      </div>

      {/* Category filter chips (unchanged) */}
      <div className="flex flex-wrap items-center gap-2" data-testid="calendar-category-filter">
        <span className="text-xs font-medium text-muted-foreground mr-1">Show:</span>
        {EVENT_CATEGORIES.map((cat) => {
          const active = !hiddenCategories.includes(cat.type);
          return (
            <button key={cat.type} type="button" onClick={() => toggleCategory(cat.type)}
              data-testid={`calendar-filter-chip-${cat.type}`} aria-pressed={active}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${active ? getEventTypeColor(cat.type) : "bg-transparent text-muted-foreground border-dashed border-gray-300 line-through opacity-60 hover:opacity-80"}`}>
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Staff color legend */}
      {activeStaff.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pb-1" data-testid="staff-legend">
          <span className="text-xs font-medium text-muted-foreground mr-1">Staff:</span>
          {activeStaff.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorForStaff(s) }} /> {firstName(s.name)}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ALL_STAFF_COLOR }} /> All staff
          </span>
        </div>
      )}

      {/* Views */}
      {calendarView === "month" && renderMonthView()}
      {calendarView === "agenda" && renderAgendaView()}
      {calendarView === "3day" && renderDayColumns(threeDayDates, "calendar-3day-view")}
      {calendarView === "week" && renderDayColumns(weekDates, "calendar-week-view")}

      {/* Day click popup */}
      <Dialog open={dayDialogOpen} onOpenChange={setDayDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dayDialogDate ? dayDialogDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : ""}</DialogTitle>
          </DialogHeader>
          {dayDialogEvents.length > 0 ? (
            <div className="space-y-3 pt-2">
              {dayDialogEvents.map((event) => {
                const c = eventColor(event);
                return (
                  <div key={event.id} className="p-3 rounded-lg border" style={{ backgroundColor: withAlpha(c, 0.1), borderColor: withAlpha(c, 0.4) }}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c }} />
                          <p className="font-medium">{event.title}</p>
                        </div>
                        {eventTimeLabel(event) && <p className="text-xs mt-1 font-medium text-slate-600">{eventTimeLabel(event)}</p>}
                        <div className="mt-1">{renderAssigneeDots(event)}</div>
                        {event.notes && <p className="text-xs mt-1 text-muted-foreground">{event.notes}</p>}
                      </div>
                      <EditDeleteButtons event={event} isAdmin={isAdmin} onEditEvent={onEditEvent} handleDeleteEvent={handleDeleteEvent} onDone={() => setDayDialogOpen(false)} testid="edit-event-dialog" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-6">No events</p>
          )}
          {isAdmin() && dayDialogDate && onAddEventForDate && (
            <div className="pt-4 mt-2 border-t">
              <Button onClick={() => { setDayDialogOpen(false); onAddEventForDate(toISO(dayDialogDate)); }} className="w-full bg-[#00305E] hover:bg-[#00305E]/90" data-testid="popup-add-on-date">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Event on This Date
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
